import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { REFERENCE_VIEW_KEY, type KnowledgeView } from "@cloudox/api-client";

/**
 * Group the views for the picker so the deterministic Reference Layer reads
 * apart from the audience lenses: the default `generic` first, the audience
 * views next, and `reference` last. Empty groups are dropped so a run that did
 * not produce one (e.g. no Reference bundle) renders no stray divider.
 */
function groupViews(views: readonly KnowledgeView[]): readonly (readonly KnowledgeView[])[] {
  const primary = views.filter((v) => v.key === "generic");
  const reference = views.filter((v) => v.key === REFERENCE_VIEW_KEY);
  const middle = views.filter((v) => v.key !== "generic" && v.key !== REFERENCE_VIEW_KEY);
  return [primary, middle, reference].filter((group) => group.length > 0);
}

/**
 * Knowledge View selector as a compact dropdown (replaces the always-expanded
 * list). The active view labels the trigger; opening reveals the full set of
 * audience lenses. Locked views are listed but disabled. Quality grades are
 * intentionally not shown here — the picker is about audience, not scoring.
 */
export interface ViewSelectProps {
  readonly views: readonly KnowledgeView[];
  /** Views present in the run but gated by the plan (rendered as locked). */
  readonly lockedViews?: readonly KnowledgeView[];
  readonly currentView?: string;
  readonly hrefFor: (viewKey: string) => string;
  /** Called after a view is chosen (e.g. to close a mobile drawer). */
  readonly onNavigate?: () => void;
}

export function ViewSelect({
  views,
  lockedViews = [],
  currentView,
  hrefFor,
  onNavigate,
}: ViewSelectProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const menuId = useId();
  // No fallback to the first view. A caller that is not on a view route (the
  // preview's overview and intelligence pages) passes nothing, and labelling
  // the trigger with a view nobody chose — and marking it `aria-selected` —
  // reports a selection the reader did not make.
  const active = currentView ? views.find((v) => v.key === currentView) : undefined;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const choose = () => {
    setOpen(false);
    onNavigate?.();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        className="flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm font-medium"
        style={{
          backgroundColor: "var(--bg-elevated)",
          border: "1px solid var(--border)",
          color: "var(--fg)",
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="truncate">{active?.displayName ?? "Select a view"}</span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden
          style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 120ms" }}
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {open ? (
        <div
          id={menuId}
          role="listbox"
          aria-label="Knowledge views"
          className="absolute left-0 right-0 z-50 mt-1.5 overflow-hidden rounded-lg py-1 shadow-xl"
          style={{ backgroundColor: "var(--bg-elevated)", border: "1px solid var(--border)" }}
        >
          {groupViews(views).map((group, groupIndex) => (
            <div key={group[0]!.key}>
              {groupIndex > 0 ? (
                <div className="my-1 border-t" style={{ borderColor: "var(--border)" }} role="separator" />
              ) : null}
              {group.map((view) => {
                const isActive = view.key === active?.key;
                return (
                  <Link
                    key={view.key}
                    to={hrefFor(view.key)}
                    role="option"
                    aria-selected={isActive}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm"
                    style={{
                      color: "var(--fg)",
                      backgroundColor: isActive ? "var(--accent-muted)" : "transparent",
                    }}
                    onClick={choose}
                  >
                    <span className="truncate flex-1">{view.displayName}</span>
                    {isActive ? <CheckIcon /> : null}
                  </Link>
                );
              })}
            </div>
          ))}

          {lockedViews.length > 0 ? (
            <div className="mt-1 border-t pt-1" style={{ borderColor: "var(--border)" }}>
              <p
                className="px-3 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-wider"
                style={{ color: "var(--fg-subtle)" }}
              >
                Not in your plan
              </p>
              {lockedViews.map((view) => (
                <div
                  key={view.key}
                  className="flex w-full cursor-not-allowed items-center gap-2 px-3 py-2 text-sm"
                  style={{ color: "var(--fg)", opacity: 0.55 }}
                  title="Upgrade to unlock this view"
                  aria-disabled
                >
                  <span className="truncate flex-1">{view.displayName}</span>
                  <LockIcon />
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2.5" aria-hidden>
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}
